import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button/Button";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import { slide1Image, slide2Image, slide3Image, slide4Image } from "./data";
import OnboardingArtwork from "./onboardingArtwork";

/** How long each slide holds before advancing on its own. */
const AUTOPLAY_MS = 3000;
/** How far a swipe has to travel before it counts as one. */
const SWIPE_THRESHOLD_PX = 60;

/** Crossfade applied to both the artwork and the copy, so they change as one. */
const fade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.3, ease: "easeOut" },
};

const OnboardingScreen = () => {
  const navigate = useNavigate();
  const translate = useLanguageTranslate();
  const [isOnbaodrdingComplete, setIsOnbaodrdingComplete] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { i18n } = useTranslation();

  const isRTL = i18n.language === "ar";

  const slides = [
    {
      title_en: "Create personalized insurance coverage in minutes",
      title_ar: "أنشئ خطة تأمينية مخصصة في دقائق",
      description: "Get insured in minutes",
      description_ar: "احصل على خطة في دقائق",
      image: slide1Image,
    },
    {
      title_en: "Easily customize your policy with easy control",
      title_ar: "خصص وثيقتك التأمينية بسهولة وتحكم",
      description: "Take control of your coverage",
      description_ar: "تحكم في تغطيتك التأمينية",
      image: slide2Image,
    },
    {
      title_en: "File claims instantly, track their status in real-time",
      title_ar: "قدم على مطالبة فوراً و تتبع حالتها في الوقت الفعلي",
      description: "Hassle-free claims process",
      description_ar: "عملية سهلة و بسيطة",
      image: slide3Image,
    },
  ];

  const slideCount = slides.length;
  const activeSlide = slides[currentSlide];

  const goToSlide = useCallback(
    (step) =>
      setCurrentSlide((current) => (current + step + slideCount) % slideCount),
    [slideCount]
  );

  // Advance on a timer, restarted by every change — so a swipe or a tap on a
  // dot gets a full dwell before the next automatic move, instead of being cut
  // short by a tick that was already in flight.
  useEffect(() => {
    if (isOnbaodrdingComplete) return undefined;
    const timer = setTimeout(() => goToSlide(1), AUTOPLAY_MS);
    return () => clearTimeout(timer);
  }, [currentSlide, isOnbaodrdingComplete, goToSlide]);

  const handleDragEnd = (event, info) => {
    // A drag to the left advances in English and goes back in Arabic, so the
    // gesture follows the direction the slides are read in.
    const travelled = info.offset.x * (isRTL ? -1 : 1);
    if (travelled <= -SWIPE_THRESHOLD_PX) goToSlide(1);
    else if (travelled >= SWIPE_THRESHOLD_PX) goToSlide(-1);
  };

  const handleGetStarted = () => {
    setIsOnbaodrdingComplete(true);
  };

  const handleSignIn = () => {
    navigate("/sign-in");
  };

  const handleRegister = () => {
    navigate("/sign-up");
  };
  const handleContinue = () => {
    navigate("/");
  };

  return (
    /* A column the height of the viewport. The illustration is a backdrop
       layer filling almost all of it; the copy and the buttons are stacked at
       the foot of the column, on top of the part of the picture the fade has
       taken to white. Because the artwork is positioned rather than a row, the
       buttons sit on the bottom edge whatever the screen height, and the
       picture is the thing that scales.
       `max-w-md` keeps the phone-shaped layout the artwork was drawn for when
       this is opened in a desktop window, rather than stretching a 459px-wide
       illustration across it. */
    <div className="onboarding-screen relative mx-auto flex w-full max-w-md flex-col bg-white">
      {!isOnbaodrdingComplete ? (
        <>
          {/* This was a react-slick carousel. slick sizes itself to its content
              and gives no way to make its internals fill a flex column — every
              `height: 100%` down its chain resolves against a parent whose
              computed height is `auto` — so the slide never stretched and the
              slider sat at the top of an empty region. Three slides with
              autoplay, dots and a swipe do not need a carousel library, and
              doing it here means both states of this screen lay out
              identically. */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`art-${currentSlide}`}
              /* `inset-0` — the picture spans the whole column and sits behind
                 the copy, the dots and the buttons alike. `overflow-hidden`
                 because object-cover crops the sides to reach full height. */
              className="absolute inset-0 overflow-hidden"
              {...fade}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.15}
              onDragEnd={handleDragEnd}
            >
              <OnboardingArtwork
                src={translate(activeSlide.image.srcEn, activeSlide.image.srcAr)}
                alt={translate(activeSlide.image.altEn, activeSlide.image.altAr)}
              />
            </motion.div>
          </AnimatePresence>

          {/* `mt-auto` on the first of the three stacked rows pushes all of
              them to the bottom of the column. */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`copy-${currentSlide}`}
              className="relative z-10 mt-auto shrink-0 px-5"
              {...fade}
            >
              <p className="text-black text-base not-italic font-medium mb-4">
                {translate(
                  activeSlide.description,
                  activeSlide.description_ar
                )}
              </p>
              <h1 className="text-black text-[28px] not-italic font-semibold leading-[120%]">
                {translate(activeSlide.title_en, activeSlide.title_ar)}
              </h1>
            </motion.div>
          </AnimatePresence>

          {/* Same two colours slick's dots were given in index.css, with the
              active one widened into a pill so progress reads at a glance. */}
          <div className="relative z-10 shrink-0 flex justify-center gap-2 pt-4">
            {slides.map((slide, index) => (
              <button
                key={slide.title_en}
                type="button"
                aria-label={translate(
                  `Go to slide ${index + 1}`,
                  `الانتقال إلى الشريحة ${index + 1}`
                )}
                aria-current={index === currentSlide}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "w-5 bg-[#005574]"
                    : "w-2 bg-[#7DB9CF]"
                }`}
              />
            ))}
          </div>

          {/* `!mb-0` because the shared .primary-button carries its own 24px
              bottom margin, and the actions block owns the spacing to the
              screen edge now — otherwise the two stack. */}
          <div className="onboarding-actions relative z-10 shrink-0 px-5 pt-5">
            <Button
              onSubmit={handleGetStarted}
              label={translate("Get Started", "البدء")}
              className="primary-button !mb-0"
            />
          </div>
        </>
      ) : (
        <>
          <div className="absolute inset-0 overflow-hidden">
            <OnboardingArtwork
              src={translate(slide4Image.srcEn, slide4Image.srcAr)}
              alt={translate(slide4Image.altEn, slide4Image.altAr)}
            />
          </div>

          <div className="relative z-10 mt-auto shrink-0 px-5">
            <p className=" text-black text-base not-italic font-medium mb-4">
              {translate(
                "Your complete insurance companion",
                "رفيقك التأميني المتكامل"
              )}
            </p>
            <h1 className="text-[28px] font-semibold text-black leading-[normal]">
              {translate(
                "Access policies, renewals, emergency services, and 24/7 support",
                "الوصول للخطط والتجديد والخدمات الطارئة والدعم على مدار الساعة"
              )}
            </h1>
          </div>

          <motion.div
            className="onboarding-actions relative z-10 shrink-0 flex w-full flex-col px-5 pt-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex gap-4 mb-4">
              <motion.div whileTap={{ scale: 0.96 }} className="flex-[1_0_0]">
                <Button
                  onSubmit={handleSignIn}
                  label={translate("Sign in", "تسجيل الدخول")}
                  className="flex flex-col items-center gap-2.5 w-full bg-[#55C6EF] p-4 rounded-[10px] max767:max-w-[100%] text-[#1A2024] text-center text-lg not-italic font-semibold leading-6 tracking-[-0.18px]"
                />
              </motion.div>
              <motion.div whileTap={{ scale: 0.96 }} className="flex-[1_0_0]">
                <Button
                  onSubmit={handleRegister}
                  label={translate("Register", "إنشاء حساب")}
                  className="flex flex-col items-center gap-2.5 w-full bg-[#55C6EF] p-4 rounded-[10px] text-[#1A2024] text-center text-lg not-italic font-semibold leading-6 tracking-[-0.18px]"
                />
              </motion.div>
            </div>
            <motion.div whileTap={{ scale: 0.97 }}>
              <Button
                onSubmit={handleContinue}
                label={translate("Continue as Guest", "المتابعة كضيف")}
                className="flex flex-col items-center gap-2.5 self-stretch border bg-white p-4 rounded-[10px] border-solid border-[#DDE2E4] w-full"
              />
            </motion.div>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default OnboardingScreen;
